import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { Building2, Check, ChevronsUpDown, Search } from 'lucide-react';

import { cn } from '@repo/utils';
import { Button } from '../../../components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/Popover';
import { getSession, getActiveOrgUnitId, setActiveOrgUnitId } from '../../../lib/session';
import { sessionQueryKey } from '../../../lib/query-client';
import { useMyOrgUnits, type MyOrgUnit, type OrgUnitType } from '../network/org.api';

interface FlatOrgUnit {
  id: string;
  name: string;
  code: string;
  fullPath: string;
  unitType: OrgUnitType;
}

export function OrgUnitSwitcher({ className }: { className?: string } = {}) {
  const [open, setOpen] = React.useState(false);
  const [selectedOrgUnitId, setSelectedOrgUnitId] = React.useState<string | null>(() => getActiveOrgUnitId());
  const queryClient = useQueryClient();

  const { data: session } = useQuery({
    queryKey: sessionQueryKey,
    queryFn: getSession,
    staleTime: Infinity,
  });

  // Self-scoped listing: every staff/agent user can see their own memberships.
  const { data: myOrgUnits, isLoading } = useMyOrgUnits();

  const flatNodes = React.useMemo<FlatOrgUnit[]>(() => {
    if (!myOrgUnits || !Array.isArray(myOrgUnits) || myOrgUnits.length < 2) return [];
    const seen = new Set<string>();
    const unique: FlatOrgUnit[] = [];
    for (const unit of myOrgUnits) {
      if (!seen.has(unit.id)) {
        seen.add(unit.id);
        unique.push({
          id: unit.id,
          name: unit.name,
          code: unit.code,
          fullPath: unit.membershipRole ? `Role: ${unit.membershipRole}` : unit.name,
          unitType: unit.unitType,
        });
      }
    }
    return unique;
  }, [myOrgUnits]);

  const activeOrgUnitId = React.useMemo(() => {
    if (selectedOrgUnitId && flatNodes.some((n) => n.id === selectedOrgUnitId)) {
      return selectedOrgUnitId;
    }
    const activeId = getActiveOrgUnitId();
    if (activeId && flatNodes.some((n) => n.id === activeId)) {
      return activeId;
    }
    const sessionDefault = session?.user?.orgUnitId;
    if (sessionDefault && flatNodes.some((n) => n.id === sessionDefault)) {
      return sessionDefault;
    }
    const primary = (myOrgUnits ?? []).find((u: MyOrgUnit) => u.isPrimary);
    return primary?.id ?? flatNodes[0]?.id ?? null;
  }, [selectedOrgUnitId, flatNodes, session, myOrgUnits]);

  const activeNode = React.useMemo(() => {
    return flatNodes.find((n) => n.id === activeOrgUnitId) || null;
  }, [flatNodes, activeOrgUnitId]);

  // Only render the switcher when there is an actual choice to make.
  if (flatNodes.length < 2) {
    return null;
  }

  const handleSelect = (id: string) => {
    if (id === activeOrgUnitId) {
      setOpen(false);
      return;
    }
    setActiveOrgUnitId(id);
    setSelectedOrgUnitId(id);
    setOpen(false);
    // Refetch everything context-dependent instead of a hard reload.
    queryClient.invalidateQueries();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'flex items-center gap-2.5 px-3 py-1.5 rounded-[6px] border border-border bg-surface-1',
            'hover:bg-surface-2 text-left transition-colors cursor-pointer select-none max-w-[160px] sm:max-w-[280px]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
            isLoading && 'opacity-60 pointer-events-none',
            className
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="org-unit-switcher-listbox"
          aria-label="Switch branch context"
          disabled={isLoading}
        >
          <Building2 className="w-4 h-4 text-accent-500 shrink-0" />
          <div className="flex flex-col min-w-0 leading-tight">
            <span className="text-caption text-text-muted uppercase tracking-wider font-semibold">
              Branch / Unit
            </span>
            <span className="text-xs font-semibold text-text-primary truncate mt-0.5">
              {isLoading ? 'Loading units...' : activeNode ? activeNode.name : 'Select Unit'}
            </span>
          </div>
          <ChevronsUpDown className="w-3.5 h-3.5 text-text-muted ml-1.5 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 border border-border bg-surface-2" style={{ width: '300px' }} align="end" sideOffset={6}>
        <Command className="flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border bg-surface-1 rounded-t-[6px]">
            <Search className="h-4 w-4 text-text-muted shrink-0" />
            <Command.Input
              placeholder="Search units..."
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls="org-unit-switcher-listbox"
              className="flex-1 bg-transparent text-xs text-text-primary placeholder:text-text-muted outline-none h-7"
            />
          </div>
          <Command.List
            id="org-unit-switcher-listbox"
            role="listbox"
            aria-label="Branch list"
            className="max-h-[260px] overflow-y-auto p-1.5 bg-surface-2 rounded-b-[6px]"
          >
            <Command.Empty className="py-6 text-center text-xs text-text-muted">
              No branch or unit found.
            </Command.Empty>
            {flatNodes.map((node) => (
              <Command.Item
                key={node.id}
                value={`${node.name} ${node.code}`}
                role="option"
                aria-selected={node.id === activeOrgUnitId}
                onSelect={() => handleSelect(node.id)}
                className={cn(
                  'relative flex cursor-pointer select-none items-center rounded-[4px] px-2.5 py-2 text-xs outline-none transition-colors mb-0.5 last:mb-0',
                  'hover:bg-surface-3 aria-selected:bg-surface-3',
                  node.id === activeOrgUnitId ? 'text-accent-700 dark:text-accent-50 bg-accent-500/5' : 'text-text-primary'
                )}
              >
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'font-semibold truncate',
                      node.id === activeOrgUnitId && 'text-accent-500'
                    )}>
                      {node.name}
                    </span>
                    <span className="text-caption font-bold uppercase tracking-wider bg-surface-4 px-1 rounded text-text-muted border border-border flex-shrink-0">
                      {node.unitType}
                    </span>
                  </div>
                  <span className="text-caption text-text-secondary truncate font-normal">
                    {node.fullPath}
                  </span>
                </div>
                <Check
                  className={cn(
                    'ml-2.5 h-3.5 w-3.5 text-accent-500 shrink-0',
                    node.id === activeOrgUnitId ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}